import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest, type User } from "@/types";
import { getNextPaginatePage } from "@/utils";

import { type TransactionTransfer } from "@/modules/recognition/user/types";

import { periods } from "./periods";

export type TransactionTransferList = Paginate<
  TransactionTransfer<true, true, true, true, true>
>;

export const transactionTransfers = createQueryKeys(
  "recognition-transaction-transfers",
  {
    allInfinite: (filter?: SearchRequest) => [{ filter }],
    meSent: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    meReceived: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    remainingPoints: (userId?: number) => [{ userId }],
    likes: (id?: number) => ({
      queryKey: [id],
      contextQueries: {
        likesInfinite: null,
      },
    }),
  },
);

const include =
  "fromUser.{position},toUser.position,value,attributes,period,likedUsers";

export const fetchTransactionTransfersQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...transactionTransfers.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<TransactionTransferList>(
          `/recognition/transactions/transfers?include=${include}`,
          {
            params: { page: pageParam },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchTransactionTransfers = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchTransactionTransfersQueryOptions(search) });

export const fetchTransactionTransfersMeReceivedQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...transactionTransfers.meReceived(page, search),
    queryFn: () =>
      api
        .get<TransactionTransferList>(
          `/recognition/transactions/transfers/me/received?include=${include}`,
          {
            params: { page },
          },
        )
        .then((response) => response.data),
  });

export const useFetchTransactionTransfersMeReceived = (
  search?: SearchRequest,
) =>
  usePaginateQuery((page) =>
    fetchTransactionTransfersMeReceivedQueryOptions(page, search),
  );

export const fetchTransactionTransfersMeSentQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...transactionTransfers.meSent(page, search),
    queryFn: () =>
      api
        .get<TransactionTransferList>(
          `/recognition/transactions/transfers/me/sent?include=${include}`,
          {
            params: { page },
          },
        )
        .then((response) => response.data),
  });

export const useFetchTransactionTransfersMeSent = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchTransactionTransfersMeSentQueryOptions(page, search),
  );

interface RemainingPointsResponse {
  remaining_points: number | null;
}

export const fetchRemainingPointsQueryOptions = (userId?: number) =>
  queryOptions({
    ...transactionTransfers.remainingPoints(userId),
    queryFn: () =>
      api
        .get<RemainingPointsResponse>(
          `/recognition/transactions/transfers/${userId}/remaining-points`,
        )
        .then((response) => response.data),
    enabled: userId !== undefined,
  });

export const useFetchRemainingPoints = (userId?: number) =>
  useQuery(fetchRemainingPointsQueryOptions(userId));

export interface TransactionTransferRequest {
  to_user_id: number;
  recognition_value_id: number;
  point_bag_id?: number | null;
  is_special?: boolean | null;
  recognition_attributes: number[];
  description?: string | null;
}

export const useCreateTransactionTransfer = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: TransactionTransferRequest) =>
      api
        .post("/recognition/transactions/transfers", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meSent._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meReceived._def,
      });
      queryClient.invalidateQueries({ queryKey: periods.one._def });
    },
  });
};

export const useUpdateSeenCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .put(`/recognition/transactions/transfers/${id}/read`)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meReceived._def,
      });
    },
  });
};

export const useLikeTransactionTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .post(`/recognition/transactions/transfers/${id}/likes`)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meSent._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meReceived._def,
      });
    },
  });
};

export const useUnlikeTransactionTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .delete(`/recognition/transactions/transfers/${id}/likes`)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meSent._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meReceived._def,
      });
    },
  });
};

export const fetchInfiniteTransactionTransferUsersLikedQueryOptions = (
  id?: number,
) =>
  infiniteQueryOptions({
    ...transactionTransfers.likes(id)._ctx.likesInfinite,
    queryFn: ({ pageParam }) =>
      api
        .get<Paginate<User<true>>>(
          `/recognition/transactions/transfers/${id}/likes`,
          {
            params: { page: pageParam, include: "position" },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: id !== undefined,
  });

export const useFetchTransactionTransferUsersLiked = (id?: number) =>
  useInfiniteQuery({
    ...fetchInfiniteTransactionTransferUsersLikedQueryOptions(id),
  });

export interface ReplyRequest {
  reply: string | null;
}

export const useReplyTransactionTransfer = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({ id, reply }: { id: number; reply: string | null }) =>
      api
        .put(`/recognition/transactions/transfers/${id}/reply`, { reply })
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meSent._def,
      });
      queryClient.invalidateQueries({
        queryKey: transactionTransfers.meReceived._def,
      });
    },
  });
};
